import { aaasHistory } from '@va/aaas/shared/router';
import { setLocale } from '@va/dashboard/actions/ui';
import { ChatwootWidget, NotificationV2 } from '@va/dashboard/components';
import { DashboardGlobalFiltersConfigProvider, DashboardReportBlockConfigProvider } from '@va/dashboard/shared/filters';
import { useDefaultTranslationOptions } from '@va/dashboard/util-hooks';
import {
  TranslationsProvider,
  fetchTranslationsFnWithFallback,
  getTranslationsUrl,
  useLocalizationContext,
} from '@va/localization';
import { RemoveAdblockerPage } from '@va/shared/remove-adblocker-page';
import { appHistory } from '@va/shared/router';
import { ConfirmationDialogContextProvider, RouterWarningProvider } from '@va/shared/util-confirmation';
import { T3_URL_PARAMS } from '@va/standalone/shared/constants';
import { isUserLoggedInWithCustomUrl } from '@va/standalone/shared/helpers';
import '@va/styles/index.scss';
import '@va/styles/tailwind.css';
import { LoaderBar } from '@va/ui/design-system';
import { ErrorBoundary } from '@va/util/components';
import { updateUrlParameter } from '@va/util/helpers';
import { DashboardAppWorker } from '@va/util/misc';
import flagsmith from 'flagsmith';
import { FlagsmithProvider } from 'flagsmith/react';
import { PropsWithChildren, useEffect } from 'react';
import { SkeletonTheme } from 'react-loading-skeleton';
import { Provider } from 'react-redux';
import { Router } from 'react-router-dom';
import { SWRConfig } from 'swr';
import { initializeApp } from './initializeApp';
import AaasRoutes from './router/routes';

const fetchTranslationsFn = async (locale = 'en') => {
  const customDomainOrigin = isUserLoggedInWithCustomUrl() ? window.location.origin : undefined;

  return fetchTranslationsFnWithFallback(getTranslationsUrl(locale, customDomainOrigin), locale);
};

const store = initializeApp();

const App = () => {
  const { isLoading: translationsLoading, translationsError } = useLocalizationContext();
  if (translationsLoading) return <LoaderBar />;
  if (translationsError) return <RemoveAdblockerPage />;

  return (
    <>
      <ChatwootWidget forcedLocale={'en'} />
      <NotificationV2 />
      <RouterWarningProvider>
        <AaasRoutes />
      </RouterWarningProvider>
    </>
  );
};

const Providers = ({ children }: PropsWithChildren) => {
  return (
    <FlagsmithProvider flagsmith={flagsmith}>
      <SWRConfig value={{ revalidateIfStale: false, revalidateOnFocus: false }}>
        <SkeletonTheme baseColor='#F9F9F9' highlightColor='#EEEEEE'>
          <Provider store={store}>
            <TranslationsProvider
              onLocaleChange={(locale) => {
                store.dispatch(setLocale(locale));
              }}
              fetchTranslations={fetchTranslationsFn}
              updateURL={(newLocale) => {
                const updatedURL = updateUrlParameter(T3_URL_PARAMS.locale, newLocale);
                appHistory.replace(updatedURL);
              }}
              useDefaultOptions={useDefaultTranslationOptions}
            >
              <ErrorBoundary>
                <Router history={aaasHistory}>
                  <DashboardReportBlockConfigProvider>
                    <ConfirmationDialogContextProvider>
                      <DashboardGlobalFiltersConfigProvider>{children}</DashboardGlobalFiltersConfigProvider>
                    </ConfirmationDialogContextProvider>
                  </DashboardReportBlockConfigProvider>
                </Router>
              </ErrorBoundary>
            </TranslationsProvider>
          </Provider>
        </SkeletonTheme>
      </SWRConfig>
    </FlagsmithProvider>
  );
};

const AaasApp = () => {
  useEffect(() => {
    DashboardAppWorker.run();
  }, []);

  return (
    <Providers>
      <App />
    </Providers>
  );
};

export default AaasApp;
